import React, { useEffect } from "react";
import { observer } from "mobx-react";
import RootStore from "../stores/RootStore";
import SingleWidgetContainer from "./SingleWidgetContainer";
import { IconButton } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";

const createWidgetButton = ({
  id,
  index,
  mobile,
  active,
  icons,
  direction,
  callback,
  storeOptions,
}) => {
  const { closeable, hasSiblings, buttonPosition, rotateButton } = storeOptions;
  if (!closeable && !hasSiblings) return null;
  const mobileStyle = {
    position: undefined,
    top: undefined,
    float: direction === "right" ? "left" : "right",
  };
  const useMobileStyle = mobile && direction !== "onMap";
  const buttonStyle = {
    position: mobile && active ? undefined : "absolute",
    top: mobile && active ? undefined : 5 + 50 * index,
    background: !active || mobile ? "white" : "#ccc",
    transition: "background 1s",
    transform: rotateButton && active ? "scaleX(-1)" : "scaleX(1)",
    // zIndex: mobile && active ? 1 : undefined,
    ...(useMobileStyle ? mobileStyle : {}),
  };
  const buttonMargin = "-7px";

  if (
    direction === "right" ||
    (direction === "onMap" && buttonPosition.includes("left"))
  ) {
    buttonStyle.left = 0;
    buttonStyle.transform =
      mobile && direction !== "onMap" && active
        ? "translate(0,0)"
        : "translate(-100%,0)";
    buttonStyle.marginLeft =
      mobile && direction !== "onMap" && active ? 0 : buttonMargin;
  }

  if (
    direction === "left" ||
    (direction === "onMap" && buttonPosition.includes("right"))
  ) {
    buttonStyle.right = 0;
    buttonStyle.transform =
      mobile && direction !== "onMap" && active
        ? "translate(0,0)"
        : "translate(+100%,0)";
    buttonStyle.marginRight =
      mobile && direction !== "onMap" && active ? 0 : buttonMargin;
  }

  if (
    direction === "bottom" ||
    (direction === "onMap" && buttonPosition.includes("top"))
  ) {
    buttonStyle.top = undefined;
    buttonStyle.left = 5 + 50 * index;
    buttonStyle.transform = "translate(0,-100%)";
    buttonStyle.marginBottom = buttonMargin;
  }
  if (
    direction === "top" ||
    (direction === "onMap" && buttonPosition.includes("bottom"))
  ) {
    buttonStyle.top = undefined;
    buttonStyle.bottom = 5 + 50 * index;
    // buttonStyle.left = 5 + 50 * index;
    buttonStyle.transform = "translate(0,+100%)";
    buttonStyle.marginTop = buttonMargin;
  }

  const Icon = active && mobile ? CloseIcon : icons[id] || SendIcon;

  return (
    <IconButton
      key={id}
      style={buttonStyle}
      // className={}
      disabled={!closeable}
      onClick={callback}
      color={active && mobile ? "secondary" : undefined}
    >
      <Icon open={active}></Icon>
    </IconButton>
  );
};

const WidgetContainer = observer(
  ({ className, mobile, direction = "right" }) => {
    const {
      activeWidgetId,
      order,
      widgets,
      icons,
      widgetAreaWidth,
      setWidgetAreaWidth,
      setMoving,
      moveListener,
      setMoveListener,
      moving,
      openWidget,
      closeWidget,
      closeable,
      hasSiblings,
      buttonPosition,
      rotateButton, // TODO: macht das in den einzelnben WidgetConfigs nicht mehr Sinn?
    } = RootStore.uiStore.subUiStores[direction];

    const buttons = order
      .map((id, index) => {
        const active = activeWidgetId === id;
        return createWidgetButton({
          id,
          index,
          mobile: mobile && active,
          active,
          icons,
          direction,
          storeOptions: {
            closeable,
            hasSiblings,
            buttonPosition,
            rotateButton,
          },
          callback: () => {
            if (active) {
              closeWidget();
            } else {
              openWidget(id);
            }
          },
        });
      })
      .filter((btn) => btn !== null);
    const active = order.reduce(
      (acc, current) => acc || current === activeWidgetId,
      false
    );
    const childWidgetCards = order.map((id, index) => {
      let widget = undefined;
      if (typeof widgets[id] === "function") {
        widget = widgets[id]();
      } else {
        widget = widgets[id];
      }
      return (
        <SingleWidgetContainer
          key={id + direction + index}
          id={id}
          index={index}
          isActive={activeWidgetId === id}
          Icon={icons[id]}
          direction={direction}
          hasSiblings={order.length > 0}
          uiStore={RootStore.uiStore.subUiStores[direction]}
          mobile={mobile}
        >
          {widget}
        </SingleWidgetContainer>
      );
    });
    useEffect(() => {
      if (moving && !moveListener) {
        const listener = (evt) => {
          const { pageX } = evt;
          const offSetWidth = document.body.offsetWidth;
          let width = offSetWidth - pageX;
          if (direction === "left") {
            width = pageX;
          }
          setWidgetAreaWidth(width);
        };
        const mouseUpListener = (evt) => {
          setMoving(false);
        };
        document.body.addEventListener("mousemove", listener);
        document.body.addEventListener("mouseup", mouseUpListener);
        setMoveListener({ listener, mouseUpListener });
      } else if (!moving && moveListener) {
        document.body.removeEventListener("mousemove", moveListener.listener);
        document.body.removeEventListener(
          "mouseup",
          moveListener.mouseUpListener
        );
        setMoveListener(null);
      }
    }, [
      moving,
      moveListener,
      setMoveListener,
      setMoving,
      setWidgetAreaWidth,
      direction,
    ]);

    const outerStyle = {
      //width: activeWidgetId === "" ? 0 : widgetAreaWidth,
    };
    if (activeWidgetId === "") {
      outerStyle.width = 0;
    } else if (mobile) {
      outerStyle.width = "100%";
    } else {
      outerStyle.width = widgetAreaWidth;
    }

    const dragBarStyle = {
      position: "absolute",
      background: "grey",
      top: 0,
      bottom: 0,
      width: "5px",
      cursor: "col-resize",
    };
    if (direction === "right") {
      dragBarStyle.left = 0;
      dragBarStyle.marginLeft = "-5px";
      // outerStyle.right = 0;
    } else if (direction === "left") {
      dragBarStyle.right = 0;
      dragBarStyle.marginRight = "-5px";
      // outerStyle.left = 0;
    } else {
      //down?
    }
    if (mobile && active) {
      outerStyle.zIndex = 3; // must be above ESRI UI (z-index: 2) so open widgets cover ESRI icons on mobile
    }
    return (
      <div style={outerStyle} className={className}>
        <div
          style={dragBarStyle}
          onMouseDown={(evt) => {
            evt.preventDefault();
            setMoving(true);
          }}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            background: "#FFF",
          }}
        >
          {mobile && active && (
            <div
              style={{
                flexGrow: 0,
                flexShrink: 0,
                height: 50,
                width: "100%",
              }}
            >
              {buttons}
            </div>
          )}
          {childWidgetCards}
        </div>
        {(!mobile || !active) && buttons}
      </div>
    );
  }
);

export default WidgetContainer;
